import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import clsx from "clsx";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import GetAppIcon from "@material-ui/icons/GetApp";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import SettingsIcon from "@material-ui/icons/Settings";
import SaveIcon from "@material-ui/icons/Save";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import ListItemText from "@material-ui/core/ListItemText";
import Tooltip from "@material-ui/core/Tooltip";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import TicketAgendaModal from "../../components/TicketAgendaModal";
import TicketOrdersModal from "../../components/TicketOrdersModal";
import TicketProcessesModal from "../../components/TicketProcessesModal";
import TicketRemindersModal from "../../components/TicketRemindersModal";
import TicketStreamingModal from "../../components/TicketStreamingModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import {
  getStoredPrivacyCensorSettings,
  privacyCensorChangedEvent,
} from "../../utils/privacyCensor";

const NO_IMPORT_CUSTOM_FIELD = "__no_import_custom_field__";
const CONTACTS_COLUMNS_STORAGE_KEY = "contacts-table-columns";
const CONTACT_COLUMN_DRAG_TYPE = "application/x-contacts-column";
const CUSTOM_COLUMN_PREFIX = "custom:";
const LABEL_FIELD_PREFIX = "Etiquetas-";
const LABELS_COLUMN_ID = "labels";
const QUICK_ACCESS_COLUMN_ID = "quickAccess";
const QUICK_ACCESS_EMPTY = "__quick_access_empty__";
const BULK_FIELD_NONE = "__bulk_field_none__";
const defaultHiddenContactColumnIds = [LABELS_COLUMN_ID, QUICK_ACCESS_COLUMN_ID];

const contactActionOptions = [
  { value: "reminders", label: "Recordatorios", emoji: "⏰" },
  { value: "agenda", label: "Agendamientos", emoji: "📅" },
  { value: "orders", label: "Pedidos", emoji: "🛒" },
  { value: "processes", label: "Procesos", emoji: "📃" },
  { value: "streaming", label: "Streaming", emoji: "📺" },
];

const defaultContactColumns = [
  { id: "avatar", label: "", type: "base", hideFromMenu: true },
  { id: "name", label: "Nombre", type: "base" },
  { id: "number", label: "Conexion", type: "base", align: "center" },
  { id: "chatId", label: "ChatId", type: "base", align: "center" },
  { id: "email", label: "Correo", type: "base", align: "center" },
  { id: "type", label: "Tipo", type: "base", align: "center" },
  { id: LABELS_COLUMN_ID, label: "Etiquetas", type: "labels", align: "center" },
  {
    id: QUICK_ACCESS_COLUMN_ID,
    label: "Acceso rapido",
    type: "quickAccess",
    align: "center",
  },
];

const getCustomColumnId = fieldName => `${CUSTOM_COLUMN_PREFIX}${fieldName}`;
const getCustomColumnName = columnId => columnId.slice(CUSTOM_COLUMN_PREFIX.length);

const readStoredColumns = userId => {
  if (typeof window === "undefined") return null;

  try {
    const value = window.localStorage.getItem(
      `${CONTACTS_COLUMNS_STORAGE_KEY}:${userId || "default"}`
    );
    return value ? JSON.parse(value) : null;
  } catch (_err) {
    return null;
  }
};

const writeStoredColumns = (userId, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      `${CONTACTS_COLUMNS_STORAGE_KEY}:${userId || "default"}`,
      JSON.stringify(value)
    );
  } catch (_err) {
    // Local persistence is a convenience; the table still works without it.
  }
};

const getReadableTextColor = color => {
  const hex = (color || "#ffffff").replace("#", "");
  const normalized =
    hex.length === 3
      ? hex.split("").map(char => `${char}${char}`).join("")
      : hex.padEnd(6, "f").slice(0, 6);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

  return luminance > 150 ? "#243040" : "#ffffff";
};

const parseCustomLabelValue = value => {
  try {
    const labels = JSON.parse(String(value || "[]"));
    if (!Array.isArray(labels)) return [];

    return labels
      .map(label => ({
        id: String(label?.id || label?.name || ""),
        name: String(label?.name || "").trim(),
        color: String(label?.color || "#757575").trim() || "#757575",
      }))
      .filter(label => label.name);
  } catch (_err) {
    return [];
  }
};

const reducer = (state, action) => {
  if (action.type === "SET_CONTACTS") {
    return action.payload;
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiTextField-root": {
        gridColumn: "1 / -1",
        width: "100%",
      },
      "& .MuiButton-root": {
        minHeight: 42,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },
      "& .MuiButton-label": {
        whiteSpace: "normal",
        lineHeight: 1.15,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  tableHeadCell: {
    whiteSpace: "nowrap",
  },
  tableHeaderContent: {
    alignItems: "center",
    display: "inline-flex",
    gap: theme.spacing(0.5),
  },
  columnHeaderCell: {
    "& $columnHoverActions": {
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 120ms ease",
    },
    "&:hover $columnHoverActions": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  columnHoverActions: {},
  columnDragHandle: {
    cursor: "grab",
    padding: 2,
    "&:active": {
      cursor: "grabbing",
    },
  },
  tableColumnDropBefore: {
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
  tableColumnDropAfter: {
    boxShadow: `inset -3px 0 0 ${theme.palette.primary.main}`,
  },
  columnMenuPaper: {
    minWidth: 260,
    maxHeight: 420,
  },
  columnMenuTitle: {
    fontWeight: 700,
    padding: theme.spacing(1, 2, 0.5),
  },
  labelCell: {
    minWidth: 260,
  },
  labelSelectValue: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    minHeight: 24,
  },
  labelChip: {
    height: 22,
    maxWidth: "100%",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  customLabelValue: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    justifyContent: "center",
    minWidth: 180,
  },
  customLabelChip: {
    height: 22,
    maxWidth: 180,
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  quickAccessCell: {
    minWidth: 190,
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileContactName: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileContactNumber: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  contactTypeChip: {
    minWidth: 72,
  },
  mobileCardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  importSteps: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  importSummary: {
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
  },
  mappingRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(2),
    alignItems: "start",
    "& > *": {
      minWidth: 0,
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  mappingAction: {
    paddingTop: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-start",
    "& .MuiButton-root": {
      whiteSpace: "nowrap",
    },
    [theme.breakpoints.down("xs")]: {
      paddingTop: 0,
    },
  },
  importSample: {
    maxHeight: 180,
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  paginationBar: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center",
      "& .MuiTablePagination-toolbar": {
        flexWrap: "wrap",
        justifyContent: "center",
        paddingLeft: 0,
        paddingRight: 0,
      },
      "& .MuiTablePagination-spacer": {
        display: "none",
      },
    },
  },
  fileInput: {
    display: "none",
  },
  censoredPhoto: {
    filter: "blur(10px)",
    transform: "scale(1.04)",
    borderRadius: "50%",
    overflow: "hidden",
  },
  censoredText: {
    filter: "blur(3px)",
    userSelect: "none",
  },
  sheetConfigFields: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 140px",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sheetConfigHint: {
    marginTop: theme.spacing(1),
  },
  sheetConfigStatus: {
    marginTop: theme.spacing(1),
    minHeight: 20,
  },
}));

const defaultImportMappings = [
  { target: "number", label: "Telefono", paths: [] },
  { target: "name", label: "Nombre", paths: [] },
  { target: "email", label: "Correo", paths: [] },
];

const bulkActions = [
  { value: "updateField", label: "Editar campo" },
  { value: "addLabels", label: "Agregar etiquetas" },
  { value: "removeLabels", label: "Quitar etiquetas" },
];

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);
  const hideResellerControls = Boolean(user?.company?.resellerBranding);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [contactCount, setContactCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkFieldId, setBulkFieldId] = useState(BULK_FIELD_NONE);
  const [bulkFieldValue, setBulkFieldValue] = useState("");
  const [bulkLabelIds, setBulkLabelIds] = useState([]);
  const [bulkLabels, setBulkLabels] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importMappings, setImportMappings] = useState(defaultImportMappings);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [sheetConfigOpen, setSheetConfigOpen] = useState(false);
  const [sheetConfigLoading, setSheetConfigLoading] = useState(false);
  const [sheetConfigSaving, setSheetConfigSaving] = useState(false);
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [contactSheetConfig, setContactSheetConfig] = useState(null);
  const [contactSheetSpreadsheetId, setContactSheetSpreadsheetId] = useState("");
  const [contactSheetName, setContactSheetName] = useState("");
  const [contactSheetStatus, setContactSheetStatus] = useState("");
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [columnPreferences, setColumnPreferences] = useState(() =>
    readStoredColumns(user?.id)
  );
  const [draggingColumnId, setDraggingColumnId] = useState("");
  const [columnDropId, setColumnDropId] = useState("");
  const [columnDropPosition, setColumnDropPosition] = useState("");
  const [contactTicketsByContactId, setContactTicketsByContactId] = useState({});
  const [labelsByConnectionId, setLabelsByConnectionId] = useState({});
  const [contactActionModal, setContactActionModal] = useState({
    action: "",
    ticket: null,
  });
  const [privacyCensorSettings, setPrivacyCensorSettings] = useState(() =>
    getStoredPrivacyCensorSettings(user?.id)
  );
  const fetchingTicketContactIdsRef = useRef(new Set());
  const fetchingLabelsConnectionIdsRef = useRef(new Set());
  const availableContactActionOptions = useMemo(
    () =>
      hideResellerControls
        ? contactActionOptions.filter(option => option.value !== "reminders")
        : contactActionOptions,
    [hideResellerControls]
  );

  useEffect(() => {
    setColumnPreferences(readStoredColumns(user?.id));
  }, [user?.id]);

  useEffect(() => {
    if (columnPreferences) {
      writeStoredColumns(user?.id, columnPreferences);
    }
  }, [columnPreferences, user?.id]);

  useEffect(() => {
    setPrivacyCensorSettings(getStoredPrivacyCensorSettings(user?.id));
  }, [user?.id]);

  const loadCustomFieldNames = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/custom-fields/names");
      setCustomFieldNames(data.fields || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    loadCustomFieldNames();
  }, [loadCustomFieldNames]);

  useEffect(() => {
    const handlePrivacySettingsChanged = event => {
      setPrivacyCensorSettings(
        event?.detail || getStoredPrivacyCensorSettings(user?.id)
      );
    };

    window.addEventListener(
      privacyCensorChangedEvent,
      handlePrivacySettingsChanged
    );

    return () => {
      window.removeEventListener(
        privacyCensorChangedEvent,
        handlePrivacySettingsChanged
      );
    };
  }, [user?.id]);

  const contactMatchesSearch = (contact, term) => {
    const search = String(term || "").trim().toLowerCase();
    if (!search) return true;

    return [
      contact?.name,
      contact?.number,
      contact?.email,
    ].some(value => String(value || "").toLowerCase().includes(search));
  };

  const mergeContactTicket = useCallback((contactId, ticket) => {
    setContactTicketsByContactId(prevState => ({
      ...prevState,
      [String(contactId)]: ticket || null,
    }));
  }, []);

  const fetchContactTicket = useCallback(async contactId => {
    if (!contactId) return;
    const normalizedContactId = String(contactId);
    if (fetchingTicketContactIdsRef.current.has(normalizedContactId)) return;
    fetchingTicketContactIdsRef.current.add(normalizedContactId);

    try {
      const { data } = await api.get(
        `/workspace-data/contacts/${normalizedContactId}/ticket`
      );
      mergeContactTicket(normalizedContactId, data.ticket || null);
    } catch (err) {
      toastError(err);
    } finally {
      fetchingTicketContactIdsRef.current.delete(normalizedContactId);
    }
  }, [mergeContactTicket]);

  const fetchLabelsForConnection = useCallback(async connectionId => {
    const normalizedConnectionId = String(connectionId || "");
    if (!normalizedConnectionId) return;
    if (labelsByConnectionId[normalizedConnectionId]) return;
    if (fetchingLabelsConnectionIdsRef.current.has(normalizedConnectionId)) return;
    fetchingLabelsConnectionIdsRef.current.add(normalizedConnectionId);

    try {
      const { data } = await api.get("/labels", {
        params: { connectionId: normalizedConnectionId },
      });
      setLabelsByConnectionId(prevState => ({
        ...prevState,
        [normalizedConnectionId]: data || [],
      }));
    } catch (err) {
      toastError(err);
    } finally {
      fetchingLabelsConnectionIdsRef.current.delete(normalizedConnectionId);
    }
  }, [labelsByConnectionId]);

  const loadContacts = useCallback(
    async ({
      nextSearchParam = searchParam,
      nextPageNumber = pageNumber,
      nextRowsPerPage = rowsPerPage,
    } = {}) => {
      setLoading(true);
      try {
        const { data } = await api.get("/contacts/", {
          params: {
            searchParam: nextSearchParam,
            pageNumber: nextPageNumber,
            rowsPerPage: nextRowsPerPage,
          },
        });
        dispatch({ type: "SET_CONTACTS", payload: data.contacts });
        setContactCount(data.count || 0);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [searchParam, pageNumber, rowsPerPage]
  );

  useEffect(() => {
    setPageNumber(1);
    setSelectedContactIds([]);
  }, [searchParam]);

  useEffect(() => {
    const contactIds = new Set(contacts.map((contact) => String(contact.id)));
    setSelectedContactIds((prevState) =>
      prevState.filter((contactId) => contactIds.has(String(contactId)))
    );
  }, [contacts]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        if (contactMatchesSearch(data.contact, searchParam)) {
          dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
        } else if (data.action === "update") {
          dispatch({ type: "DELETE_CONTACT", payload: +data.contact.id });
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [searchParam]);

  const customColumns = useMemo(() => {
    const names = new Set(customFieldNames.filter(Boolean));

    contacts.forEach(contact => {
      (contact.extraInfo || []).forEach(info => {
        if (info?.name) names.add(info.name);
      });
    });

    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map(fieldName => ({
        id: getCustomColumnId(fieldName),
        label: fieldName,
        type: "custom",
        align: "center",
      }));
  }, [contacts, customFieldNames]);

  const allContactColumns = useMemo(
    () =>
      [
        { id: "avatar", label: "", type: "base", hideFromMenu: true },
        {
          id: "name",
          label: i18n.t("contacts.table.name"),
          type: "base",
        },
        {
          id: "number",
          label: i18n.t("contacts.table.connection"),
          type: "base",
          align: "center",
        },
        { id: "chatId", label: "ChatId", type: "base", align: "center" },
        {
          id: "email",
          label: i18n.t("contacts.table.email"),
          type: "base",
          align: "center",
        },
        { id: "type", label: "Tipo", type: "base", align: "center" },
        {
          id: LABELS_COLUMN_ID,
          label: "Etiquetas",
          type: "labels",
          align: "center",
        },
        {
          id: QUICK_ACCESS_COLUMN_ID,
          label: "Acceso rapido",
          type: "quickAccess",
          align: "center",
        },
        ...customColumns,
      ],
    [customColumns]
  );

  const getEffectiveHiddenColumnIds = useCallback(preferences => {
    const hidden = new Set(
      preferences?.hidden || defaultHiddenContactColumnIds
    );
    const order = Array.isArray(preferences?.order) ? preferences.order : [];

    defaultHiddenContactColumnIds.forEach(columnId => {
      if (!order.includes(columnId)) hidden.add(columnId);
    });

    return hidden;
  }, []);

  const orderedColumns = useMemo(() => {
    const order = Array.isArray(columnPreferences?.order)
      ? columnPreferences.order
      : defaultContactColumns.map(column => column.id);
    const columnById = new Map(allContactColumns.map(column => [column.id, column]));
    const usedIds = new Set();
    const sorted = [];

    order.forEach(columnId => {
      const column = columnById.get(columnId);
      if (column && !usedIds.has(column.id)) {
        sorted.push(column);
        usedIds.add(column.id);
      }
    });

    allContactColumns.forEach(column => {
      if (!usedIds.has(column.id)) sorted.push(column);
    });

    return sorted;
  }, [allContactColumns, columnPreferences]);

  const visibleColumns = useMemo(() => {
    const hidden = getEffectiveHiddenColumnIds(columnPreferences);
    return orderedColumns.filter(column => !hidden.has(column.id));
  }, [orderedColumns, columnPreferences, getEffectiveHiddenColumnIds]);

  const labelsColumnVisible = visibleColumns.some(
    column => column.id === LABELS_COLUMN_ID
  );
  const quickAccessColumnVisible = visibleColumns.some(
    column => column.id === QUICK_ACCESS_COLUMN_ID
  );
  const ticketBackedColumnVisible = labelsColumnVisible || quickAccessColumnVisible;

  const getContactCustomFieldValue = (contact, fieldName) => {
    const field = (contact.extraInfo || []).find(
      info => String(info?.name || "") === String(fieldName)
    );

    return field?.value || "-";
  };

  const renderContactCustomFieldValue = (contact, fieldName) => {
    const value = getContactCustomFieldValue(contact, fieldName);
    if (!String(fieldName || "").startsWith(LABEL_FIELD_PREFIX)) return value;

    const labels = parseCustomLabelValue(value);
    if (!labels.length) return "-";

    return (
      <div className={classes.customLabelValue}>
        {labels.map(label => (
          <Chip
            key={`${fieldName}-${label.id}-${label.name}`}
            className={classes.customLabelChip}
            label={label.name}
            size="small"
            style={{
              backgroundColor: label.color,
              color: getReadableTextColor(label.color),
            }}
          />
        ))}
      </div>
    );
  };

  const getColumnLabel = column => column.label || "";

  const setNextColumnPreferences = updater => {
    setColumnPreferences(prevState => {
      const current = prevState || {
        order: orderedColumns.map(column => column.id),
        hidden: defaultHiddenContactColumnIds,
      };
      return updater(current);
    });
  };

  const toggleColumnVisibility = columnId => {
    setNextColumnPreferences(current => {
      const hidden = getEffectiveHiddenColumnIds(current);
      if (hidden.has(columnId)) {
        hidden.delete(columnId);
      } else {
        hidden.add(columnId);
      }

      return {
        ...current,
        order: orderedColumns.map(column => column.id),
        hidden: Array.from(hidden),
      };
    });
  };

  useEffect(() => {
    if (!ticketBackedColumnVisible) return;

    contacts.forEach(contact => {
      const contactId = String(contact.id);
      if (Object.prototype.hasOwnProperty.call(contactTicketsByContactId, contactId)) {
        return;
      }
      fetchContactTicket(contactId);
    });
  }, [
    contacts,
    contactTicketsByContactId,
    fetchContactTicket,
    ticketBackedColumnVisible,
  ]);

  useEffect(() => {
    if (!labelsColumnVisible) return;

    Object.values(contactTicketsByContactId).forEach(ticket => {
      if (ticket?.connectionId) {
        fetchLabelsForConnection(ticket.connectionId);
      }
    });
  }, [
    contactTicketsByContactId,
    fetchLabelsForConnection,
    labelsColumnVisible,
  ]);

  const handleColumnDragStart = columnId => event => {
    event.dataTransfer.setData(CONTACT_COLUMN_DRAG_TYPE, String(columnId));
    event.dataTransfer.effectAllowed = "move";
    setDraggingColumnId(String(columnId));
  };

  const handleColumnDragEnd = () => {
    setDraggingColumnId("");
    setColumnDropId("");
    setColumnDropPosition("");
  };

  const handleColumnDragOver = columnId => event => {
    if (!draggingColumnId || draggingColumnId === columnId) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const placement = event.clientX > bounds.left + bounds.width / 2 ? "after" : "before";
    setColumnDropId(prevState =>
      prevState === String(columnId) ? prevState : String(columnId)
    );
    setColumnDropPosition(prevState =>
      prevState === placement ? prevState : placement
    );
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleColumnDrop = targetColumnId => event => {
    const draggedColumnId = event.dataTransfer.getData(CONTACT_COLUMN_DRAG_TYPE);
    if (!draggedColumnId || draggedColumnId === targetColumnId) return;

    event.preventDefault();
    setNextColumnPreferences(current => {
      const currentOrder = orderedColumns.map(column => column.id);
      const sourceIndex = currentOrder.findIndex(columnId => columnId === draggedColumnId);
      const targetIndex = currentOrder.findIndex(columnId => columnId === targetColumnId);
      if (sourceIndex < 0 || targetIndex < 0) return current;

      const bounds = event.currentTarget.getBoundingClientRect();
      const shouldInsertAfter = event.clientX > bounds.left + bounds.width / 2;
      let insertIndex = targetIndex + (shouldInsertAfter ? 1 : 0);
      const [movedColumnId] = currentOrder.splice(sourceIndex, 1);
      if (sourceIndex < insertIndex) insertIndex -= 1;
      currentOrder.splice(insertIndex, 0, movedColumnId);

      return { ...current, order: currentOrder };
    });
    handleColumnDragEnd();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
    setSelectedContactIds([]);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPageNumber(1);
    setSelectedContactIds([]);
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        userId: user?.id,
        status: "open",
      });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      await loadContacts({ nextSearchParam: "", nextPageNumber: 1 });
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const allContactsSelected =
    contacts.length > 0 &&
    contacts.every((contact) => selectedContactIds.includes(String(contact.id)));
  const selectedContactCount = selectedContactIds.length;
  const selectedContacts = useMemo(
    () =>
      contacts.filter(contact =>
        selectedContactIds.includes(String(contact.id))
      ),
    [contacts, selectedContactIds]
  );
  const bulkFieldOptions = useMemo(() => {
    const baseFields = [
      { id: "name", label: i18n.t("contacts.table.name") },
      { id: "number", label: i18n.t("contacts.table.connection") },
      { id: "email", label: i18n.t("contacts.table.email") },
    ];

    const customFields = customFieldNames.map(fieldName => ({
      id: getCustomColumnId(fieldName),
      label: fieldName,
    }));

    return [...baseFields, ...customFields];
  }, [customFieldNames]);

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkAction("");
    setBulkFieldId(BULK_FIELD_NONE);
    setBulkFieldValue("");
    setBulkLabelIds([]);
  };

  const loadBulkLabels = async () => {
    try {
      const { data } = await api.get("/labels");
      setBulkLabels(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  const openBulkModal = () => {
    setBulkModalOpen(true);
    loadBulkLabels();
    loadCustomFieldNames();
  };

  const buildBulkContactPayload = (contact) => {
    const payload = {
      name: contact.name || "",
      email: contact.email || "",
      extraInfo: contact.extraInfo || [],
    };

    if (contact.number || bulkFieldId === "number") {
      payload.number = bulkFieldId === "number" ? bulkFieldValue : contact.number;
    }

    if (bulkFieldId === "name" || bulkFieldId === "email") {
      payload[bulkFieldId] = bulkFieldValue;
      return payload;
    }

    if (bulkFieldId.startsWith(CUSTOM_COLUMN_PREFIX)) {
      const fieldName = getCustomColumnName(bulkFieldId);
      const extraInfo = [...(contact.extraInfo || [])];
      const fieldIndex = extraInfo.findIndex(
        info => String(info?.name || "") === fieldName
      );

      if (fieldIndex >= 0) {
        extraInfo[fieldIndex] = { ...extraInfo[fieldIndex], value: bulkFieldValue };
      } else if (fieldName) {
        extraInfo.push({ name: fieldName, value: bulkFieldValue });
      }

      payload.extraInfo = extraInfo;
    }

    return payload;
  };

  const getTicketForBulkContact = async contact => {
    const cachedTicket = contactTicketsByContactId[String(contact.id)];
    if (Object.prototype.hasOwnProperty.call(contactTicketsByContactId, String(contact.id))) {
      return cachedTicket;
    }

    const { data } = await api.get(`/workspace-data/contacts/${contact.id}/ticket`);
    mergeContactTicket(contact.id, data.ticket || null);
    return data.ticket || null;
  };

  const handleBulkApply = async () => {
    if (!bulkAction || !selectedContacts.length) return;

    setBulkSaving(true);
    try {
      if (bulkAction === "updateField") {
        const updatedContacts = await Promise.all(
          selectedContacts.map(async contact => {
            const { data } = await api.put(
              `/contacts/${contact.id}`,
              buildBulkContactPayload(contact)
            );
            return data;
          })
        );
        updatedContacts.forEach(contact =>
          dispatch({ type: "UPDATE_CONTACTS", payload: contact })
        );
        await loadCustomFieldNames();
        toast.success("Contactos actualizados");
      } else {
        const tickets = (
          await Promise.all(selectedContacts.map(getTicketForBulkContact))
        ).filter(ticket => ticket?.id);

        if (!tickets.length) {
          toast.error("Los contactos seleccionados no tienen tickets disponibles.");
          return;
        }

        const { data } = await api.put("/tickets/bulk", {
          action: bulkAction,
          ticketIds: tickets.map(ticket => ticket.id),
          labelIds: bulkLabelIds,
        });
        (data.tickets || []).forEach(updateCachedTicket);
        toast.success("Etiquetas actualizadas");
      }

      setSelectedContactIds([]);
      closeBulkModal();
    } catch (err) {
      toastError(err);
    } finally {
      setBulkSaving(false);
    }
  };

  const bulkActionRequiresField = bulkAction === "updateField";
  const bulkActionRequiresLabels = ["addLabels", "removeLabels"].includes(bulkAction);
  const bulkApplyDisabled =
    bulkSaving ||
    !bulkAction ||
    !selectedContacts.length ||
    (bulkActionRequiresField && bulkFieldId === BULK_FIELD_NONE) ||
    (bulkActionRequiresLabels && !bulkLabelIds.length);

  const toggleContactSelection = (contactId) => {
    const normalizedId = String(contactId);
    setSelectedContactIds((prevState) =>
      prevState.includes(normalizedId)
        ? prevState.filter((id) => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllContactsSelection = () => {
    if (allContactsSelected) {
      setSelectedContactIds([]);
      return;
    }

    setSelectedContactIds(contacts.map((contact) => String(contact.id)));
  };

  const handleDeleteSelectedContacts = async () => {
    try {
      await Promise.all(
        selectedContactIds.map((contactId) => api.delete(`/contacts/${contactId}`))
      );
      selectedContactIds.forEach((contactId) =>
        dispatch({ type: "DELETE_CONTACT", payload: Number(contactId) })
      );
      setSelectedContactIds([]);
      toast.success(i18n.t("contacts.toasts.deleted"));
      await loadContacts({ nextSearchParam: "", nextPageNumber: 1 });
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
      setSearchParam("");
      setPageNumber(1);
    }
  };

  const handleOpenExportMenu = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportAnchorEl(null);
  };

  const loadContactSheetConfig = async () => {
    setSheetConfigLoading(true);
    setContactSheetStatus("");
    try {
      const { data } = await api.get("/contacts/sheets/config");
      const config = data.config || null;
      setContactSheetConfig(config);
      setContactSheetSpreadsheetId(config?.spreadsheetId || "");
      setContactSheetName(config?.sheetName || "");
    } catch (err) {
      toastError(err);
    } finally {
      setSheetConfigLoading(false);
    }
  };

  const openSheetConfigModal = () => {
    setSheetConfigOpen(true);
    loadContactSheetConfig();
  };

  const closeSheetConfigModal = () => {
    if (sheetConfigSaving || sheetSyncing) return;
    setSheetConfigOpen(false);
  };

  const saveContactSheetConfig = async ({ showToast = true } = {}) => {
    const spreadsheetId = contactSheetSpreadsheetId.trim();
    const sheetName = contactSheetName.trim();

    if (!spreadsheetId || !sheetName) {
      setContactSheetStatus("Completa el ID del documento y el nombre de la hoja.");
      return null;
    }

    setSheetConfigSaving(true);
    try {
      const { data } = await api.put("/contacts/sheets/config", {
        spreadsheetId,
        sheetName,
      });
      setContactSheetConfig(data);
      setContactSheetSpreadsheetId(data.spreadsheetId || spreadsheetId);
      setContactSheetName(data.sheetName || sheetName);
      setContactSheetStatus("Configuracion guardada.");
      if (showToast) toast.success("Configuracion de contactos guardada.");
      return data;
    } catch (err) {
      toastError(err);
      return null;
    } finally {
      setSheetConfigSaving(false);
    }
  };

  const syncContactsToSheet = async () => {
    const savedConfig = await saveContactSheetConfig({ showToast: false });
    if (!savedConfig) return;

    setSheetSyncing(true);
    setContactSheetStatus("Enviando contactos a Google Sheets...");
    try {
      const { data } = await api.post("/contacts/sheets/sync");
      setContactSheetStatus(
        `${data.total || 0} contacto(s) enviados a Google Sheets.`
      );
      toast.success("Contactos enviados a Google Sheets.");
    } catch (err) {
      setContactSheetStatus(
        "No se pudieron enviar los contactos. Revisa el Apps Script."
      );
      toastError(err);
    } finally {
      setSheetSyncing(false);
    }
  };

  const handleExportContacts = async (format) => {
    try {
      const { data } = await api.get("/contacts/export", {
        params: { searchParam, format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `contactos-${new Date().toISOString().slice(0, 10)}.${format}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    } finally {
      handleCloseExportMenu();
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportLoading(false);
    setImportFile(null);
    setImportPreview(null);
    setImportMappings(defaultImportMappings);
  };

  const openImportModal = async () => {
    setImportModalOpen(true);
    await loadCustomFieldNames();
  };

  const handleImportPreview = async () => {
    if (!importFile) {
      toast.error("Selecciona un archivo CSV o XML.");
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const { data } = await api.post("/contacts/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data);
      setImportMappings(defaultImportMappings);
    } catch (err) {
      toastError(err);
    } finally {
      setImportLoading(false);
    }
  };

  const updateImportMapping = (index, patch) => {
    setImportMappings((prevState) =>
      prevState.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...patch } : mapping
      )
    );
  };

  const addImportCustomMapping = () => {
    setImportMappings((prevState) => [
      ...prevState,
      { target: "extra", label: "", paths: [], isNew: false },
    ]);
  };

  const removeImportCustomMapping = (index) => {
    setImportMappings((prevState) =>
      prevState.filter((_mapping, mappingIndex) => mappingIndex !== index)
    );
  };

  const handleImportCommit = async () => {
    const phoneMapping = importMappings.find((mapping) => mapping.target === "number");
    if (!phoneMapping?.paths?.length) {
      toast.error("Mapea el campo Telefono antes de importar.");
      return;
    }
    if (!importFile) {
      toast.error("Selecciona un archivo CSV o XML.");
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("mappings", JSON.stringify(importMappings));
      const { data } = await api.post("/contacts/import/commit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: contactData } = await api.get("/contacts/", {
        params: { searchParam: "", pageNumber: 1, rowsPerPage },
      });
      dispatch({ type: "RESET" });
      dispatch({ type: "SET_CONTACTS", payload: contactData.contacts || [] });
      setContactCount(contactData.count || 0);
      setPageNumber(1);
      setSearchParam("");
      setSelectedContactIds([]);
      toast.success(
        `Importacion lista. Creados: ${data.created || 0}. Actualizados: ${
          data.updated || 0
        }. Omitidos: ${data.skipped || 0}.`
      );
      await loadCustomFieldNames();
      closeImportModal();
    } catch (err) {
      toastError(err);
    } finally {
      setImportLoading(false);
    }
  };

  const renderImportMappingRow = (mapping, index) => {
    const fields = importPreview?.fields || [];
    const label =
      mapping.target === "extra" ? (
        <>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Campo personalizado</InputLabel>
            <Select
              value={mapping.isNew ? "__new__" : mapping.label || NO_IMPORT_CUSTOM_FIELD}
              onChange={(event) => {
                if (event.target.value === "__new__") {
                  updateImportMapping(index, { label: "", isNew: true });
                  return;
                }

                updateImportMapping(index, {
                  label:
                    event.target.value === NO_IMPORT_CUSTOM_FIELD
                      ? ""
                      : event.target.value,
                  isNew: false,
                });
              }}
              label="Campo personalizado"
            >
              <MenuItem value={NO_IMPORT_CUSTOM_FIELD}>Seleccionar campo</MenuItem>
              {customFieldNames.map((fieldName) => (
                <MenuItem key={fieldName} value={fieldName}>
                  {fieldName}
                </MenuItem>
              ))}
              <MenuItem value="__new__">Crear nuevo...</MenuItem>
            </Select>
          </FormControl>
          {mapping.isNew && (
            <TextField
              label="Nuevo campo"
              value={mapping.label || ""}
              onChange={(event) =>
                updateImportMapping(index, { label: event.target.value })
              }
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
            />
          )}
        </>
      ) : (
        <Typography variant="body2">{mapping.label}</Typography>
      );

    return (
      <div className={classes.mappingRow} key={`import-mapping-${index}`}>
        {label}
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>Campo del archivo</InputLabel>
          <Select
            multiple
            value={mapping.paths || []}
            onChange={(event) =>
              updateImportMapping(index, { paths: event.target.value })
            }
            label="Campo del archivo"
            renderValue={(selected) => selected.join(", ")}
          >
            {fields.map((field) => (
              <MenuItem key={field} value={field}>
                <Checkbox
                  color="primary"
                  checked={(mapping.paths || []).indexOf(field) > -1}
                />
                {field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={classes.mappingAction}>
          {mapping.target === "extra" && (
            <Button
              size="small"
              color="secondary"
              onClick={() => removeImportCustomMapping(index)}
            >
              Quitar campo
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderContactActions = (contact) => (
    <>
      {!hideResellerControls && (
        <IconButton size="small" onClick={() => handleSaveTicket(contact.id)}>
          <WhatsAppIcon />
        </IconButton>
      )}
      <IconButton size="small" onClick={() => hadleEditContact(contact.id)}>
        <EditIcon />
      </IconButton>
      <Can
        role={user.profile}
        perform="contacts-page:deleteContact"
        yes={() => (
          <IconButton
            size="small"
            onClick={(e) => {
              setConfirmOpen(true);
              setDeletingContact(contact);
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        )}
      />
    </>
  );

  const renderContactType = (contact) => (
    <Chip
      className={classes.contactTypeChip}
      size="small"
      color={contact.isGroup ? "primary" : "default"}
      label={contact.isGroup ? "Grupo" : "Persona"}
    />
  );

  const updateCachedTicket = ticket => {
    if (!ticket?.contactId) return;
    mergeContactTicket(ticket.contactId, ticket);
  };

  const getLabelsForTicket = ticket => {
    const loadedLabels = labelsByConnectionId[String(ticket?.connectionId || "")] || [];
    const byId = new Map(loadedLabels.map(label => [String(label.id), label]));
    (ticket?.labels || []).forEach(label => {
      if (label?.id) byId.set(String(label.id), label);
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  };

  const renderTicketLabelsValue = (selected, labels) => (
    <div className={classes.labelSelectValue}>
      {selected.length ? (
        labels
          .filter(label => selected.some(labelId => String(labelId) === String(label.id)))
          .map(label => (
            <Chip
              key={label.id}
              label={label.name}
              className={classes.labelChip}
              style={{
                backgroundColor: label.color,
                color: getReadableTextColor(label.color),
              }}
            />
          ))
      ) : (
        <span>&ZeroWidthSpace;</span>
      )}
    </div>
  );

  const handleTicketLabelsChange = async (contact, labelIds) => {
    const ticket = contactTicketsByContactId[String(contact.id)];
    if (!ticket?.id) return;

    const previousTicket = ticket;
    const labels = labelsByConnectionId[String(ticket.connectionId || "")] || [];
    const optimisticTicket = {
      ...ticket,
      labels: labelIds
        .map(labelId => labels.find(label => String(label.id) === String(labelId)))
        .filter(Boolean),
    };

    mergeContactTicket(contact.id, optimisticTicket);

    try {
      const { data } = await api.put(
        `/workspace-data/tickets/${ticket.id}/labels`,
        { labelIds }
      );
      updateCachedTicket(data.ticket);
    } catch (err) {
      mergeContactTicket(contact.id, previousTicket);
      toastError(err);
    }
  };

  const renderTicketLabelsCell = contact => {
    const ticket = contactTicketsByContactId[String(contact.id)];
    const labels = getLabelsForTicket(ticket);
    const selectedLabelIds = (ticket?.labels || []).map(label => label.id);
    const disabled = !ticket?.id;

    return (
      <FormControl
        className={classes.labelCell}
        margin="dense"
        size="small"
        variant="outlined"
        disabled={disabled}
      >
        <InputLabel>Etiquetas</InputLabel>
        <Select
          multiple
          value={selectedLabelIds}
          onOpen={() => {
            fetchContactTicket(contact.id);
            if (ticket?.connectionId) fetchLabelsForConnection(ticket.connectionId);
          }}
          onChange={event => handleTicketLabelsChange(contact, event.target.value)}
          label="Etiquetas"
          renderValue={selected => renderTicketLabelsValue(selected, labels)}
        >
          {labels.map(label => (
            <MenuItem key={label.id} value={label.id}>
              <Checkbox
                style={{ color: label.color }}
                checked={selectedLabelIds.some(
                  labelId => String(labelId) === String(label.id)
                )}
              />
              <ListItemText primary={label.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const getContactActionOption = action =>
    contactActionOptions.find(option => option.value === action) ||
    contactActionOptions[0];

  const handleContactAction = (contact, action) => {
    const ticket = contactTicketsByContactId[String(contact.id)];
    if (!ticket?.id) return;

    const option = getContactActionOption(action);
    setContactActionModal({
      action: option.value,
      ticket,
    });
  };

  const closeContactActionModal = () => {
    const contactId = contactActionModal.ticket?.contactId;

    setContactActionModal({
      action: "",
      ticket: null,
    });

    if (contactId) {
      fetchContactTicket(contactId);
    }
  };

  const renderQuickAccessCell = contact => {
    const ticket = contactTicketsByContactId[String(contact.id)];
    const disabled = !ticket?.id;

    return (
      <Tooltip
        title={disabled ? "Este contacto no tiene un ticket disponible" : "Acceso rapido"}
      >
        <span>
          <FormControl
            className={classes.quickAccessCell}
            margin="dense"
            size="small"
            variant="outlined"
            disabled={disabled}
          >
            <InputLabel>Accion</InputLabel>
            <Select
              value={QUICK_ACCESS_EMPTY}
              onOpen={() => fetchContactTicket(contact.id)}
              onChange={event => {
                if (event.target.value === QUICK_ACCESS_EMPTY) return;
                handleContactAction(contact, event.target.value);
              }}
              label="Accion"
              renderValue={() => "Acceso rapido"}
            >
              <MenuItem value={QUICK_ACCESS_EMPTY} disabled>
                Acceso rapido
              </MenuItem>
              {availableContactActionOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.emoji} {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </span>
      </Tooltip>
    );
  };

  const renderContactColumnValue = (contact, column) => {
    const privateTextClass = privacyCensorSettings.contactNames
      ? classes.censoredText
      : undefined;

    if (column.id === "avatar") {
      return (
        <Avatar
          src={
            privacyCensorSettings.contactPhotos ? undefined : contact.profilePicUrl
          }
          className={
            privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined
          }
        />
      );
    }

    if (column.id === "name") {
      return (
        <span className={privateTextClass}>
          {privacyCensorSettings.contactNames ? "Contacto privado" : contact.name}
        </span>
      );
    }

    if (column.id === "number") {
      return (
        <span className={privateTextClass}>
          {privacyCensorSettings.contactNames
            ? "Telefono privado"
            : contact.number || "-"}
        </span>
      );
    }

    if (column.id === "chatId") {
      return (
        <span className={privateTextClass}>
          {privacyCensorSettings.contactNames
            ? "ChatId privado"
            : contact.chatId || "-"}
        </span>
      );
    }

    if (column.id === "email") {
      return (
        <span className={privateTextClass}>
          {privacyCensorSettings.contactNames
            ? "Correo privado"
            : contact.email || "-"}
        </span>
      );
    }

    if (column.id === "type") {
      return renderContactType(contact);
    }

    if (column.id === LABELS_COLUMN_ID) {
      return renderTicketLabelsCell(contact);
    }

    if (column.id === QUICK_ACCESS_COLUMN_ID) {
      return renderQuickAccessCell(contact);
    }

    if (column.type === "custom") {
      if (privacyCensorSettings.contactNames) {
        return <span className={privateTextClass}>Dato privado</span>;
      }

      return renderContactCustomFieldValue(contact, getCustomColumnName(column.id));
    }

    return "-";
  };

  const renderColumnHeader = column => (
    <TableCell
      key={column.id}
      align={column.align}
      className={`${classes.tableHeadCell} ${classes.columnHeaderCell} ${
        columnDropId === String(column.id) && columnDropPosition === "before"
          ? classes.tableColumnDropBefore
          : ""
      } ${
        columnDropId === String(column.id) && columnDropPosition === "after"
          ? classes.tableColumnDropAfter
          : ""
      }`}
      onDragOver={handleColumnDragOver(column.id)}
      onDrop={handleColumnDrop(column.id)}
    >
      <span className={classes.tableHeaderContent}>
        <span className={classes.columnHoverActions}>
          <Tooltip title="Mover columna">
            <IconButton
              className={classes.columnDragHandle}
              size="small"
              draggable
              onDragStart={handleColumnDragStart(column.id)}
              onDragEnd={handleColumnDragEnd}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </span>
        {getColumnLabel(column)}
      </span>
    </TableCell>
  );

  const renderColumnsMenu = () => {
    const hidden = getEffectiveHiddenColumnIds(columnPreferences);

    return (
      <Menu
        anchorEl={columnsAnchorEl}
        open={Boolean(columnsAnchorEl)}
        onClose={() => setColumnsAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: classes.columnMenuPaper }}
      >
        <Typography variant="body2" className={classes.columnMenuTitle}>
          Columnas visibles
        </Typography>
        {orderedColumns
          .filter(column => !column.hideFromMenu)
          .map(column => (
            <MenuItem key={column.id} onClick={() => toggleColumnVisibility(column.id)}>
              <Checkbox
                color="primary"
                checked={!hidden.has(column.id)}
                tabIndex={-1}
              />
              <ListItemText primary={getColumnLabel(column)} />
            </MenuItem>
          ))}
      </Menu>
    );
  };

  const renderMobileContactCard = (contact) => (
    <div className={classes.mobileCard} key={contact.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedContactIds.includes(String(contact.id))}
          onChange={() => toggleContactSelection(contact.id)}
          inputProps={{ "aria-label": `Seleccionar ${contact.name}` }}
        />
        <Avatar
          src={
            privacyCensorSettings.contactPhotos ? undefined : contact.profilePicUrl
          }
          className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        />
        <div>
          <Typography
            className={clsx(classes.mobileContactName, {
              [classes.censoredText]: privacyCensorSettings.contactNames,
            })}
          >
            {privacyCensorSettings.contactNames ? "Contacto privado" : contact.name}
          </Typography>
          <Typography
            className={clsx(classes.mobileContactNumber, {
              [classes.censoredText]: privacyCensorSettings.contactNames,
            })}
          >
            {privacyCensorSettings.contactNames
              ? "Telefono privado"
              : contact.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>{renderContactActions(contact)}</div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        {visibleColumns
          .filter(column => !["avatar", "name"].includes(column.id))
          .map(column => (
            <div className={classes.mobileField} key={column.id}>
              <span className={classes.mobileLabel}>{getColumnLabel(column)}</span>
              <div className={classes.mobileValue}>
                {renderContactColumnValue(contact, column)}
              </div>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <MainContainer className={classes.mainContainer}>
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ConfirmationModal
        title={`Eliminar ${selectedContactCount} contacto${
          selectedContactCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedContacts}
      >
        {i18n.t("contacts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog open={bulkModalOpen} onClose={closeBulkModal} fullWidth maxWidth="sm">
        <DialogTitle>Gestionar contactos seleccionados</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            {selectedContactCount} contacto(s) seleccionados
          </Typography>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Accion</InputLabel>
            <Select
              value={bulkAction}
              onChange={event => {
                setBulkAction(event.target.value);
                setBulkFieldId(BULK_FIELD_NONE);
                setBulkFieldValue("");
                setBulkLabelIds([]);
              }}
              label="Accion"
            >
              {bulkActions.map(action => (
                <MenuItem key={action.value} value={action.value}>
                  {action.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {bulkActionRequiresField && (
            <>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Campo</InputLabel>
                <Select
                  value={bulkFieldId}
                  onChange={event => setBulkFieldId(event.target.value)}
                  label="Campo"
                >
                  <MenuItem value={BULK_FIELD_NONE}>Seleccionar campo</MenuItem>
                  {bulkFieldOptions.map(field => (
                    <MenuItem key={field.id} value={field.id}>
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Valor"
                value={bulkFieldValue}
                onChange={event => setBulkFieldValue(event.target.value)}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </>
          )}
          {bulkActionRequiresLabels && (
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Etiquetas</InputLabel>
              <Select
                multiple
                value={bulkLabelIds}
                onChange={event => setBulkLabelIds(event.target.value)}
                label="Etiquetas"
                renderValue={selected =>
                  bulkLabels
                    .filter(label =>
                      selected.some(labelId => String(labelId) === String(label.id))
                    )
                    .map(label => label.name)
                    .join(", ")
                }
              >
                {bulkLabels.map(label => (
                  <MenuItem key={label.id} value={label.id}>
                    <Checkbox
                      style={{ color: label.color }}
                      checked={bulkLabelIds.some(
                        labelId => String(labelId) === String(label.id)
                      )}
                    />
                    <ListItemText primary={label.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkModal} variant="contained">
            Cancelar
          </Button>
          <Button
            onClick={handleBulkApply}
            color="primary"
            variant="contained"
            disabled={bulkApplyDisabled}
          >
            {bulkSaving ? "Aplicando..." : "Aplicar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={sheetConfigOpen}
        onClose={closeSheetConfigModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Google Sheets</DialogTitle>
        <DialogContent dividers>
          {sheetConfigLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <div className={classes.sheetConfigFields}>
                <TextField
                  label="ID del documento"
                  value={contactSheetSpreadsheetId}
                  onChange={event =>
                    setContactSheetSpreadsheetId(event.target.value)
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
                <TextField
                  label="Hoja"
                  value={contactSheetName}
                  onChange={event => setContactSheetName(event.target.value)}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              </div>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.sheetConfigStatus}
              >
                {contactSheetStatus ||
                  (contactSheetConfig
                    ? "Configuracion lista para enviar contactos."
                    : "")}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeSheetConfigModal}
            disabled={sheetConfigSaving || sheetSyncing}
          >
            Cerrar
          </Button>
          <Button
            onClick={() => saveContactSheetConfig()}
            color="primary"
            variant="outlined"
            startIcon={<SaveIcon />}
            disabled={sheetConfigSaving || sheetSyncing || sheetConfigLoading}
          >
            {sheetConfigSaving ? "Guardando..." : "Guardar"}
          </Button>
          <Button
            onClick={syncContactsToSheet}
            color="primary"
            variant="contained"
            disabled={sheetConfigSaving || sheetSyncing || sheetConfigLoading}
          >
            {sheetSyncing ? "Enviando..." : "Guardar y enviar"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : ""
        }
        open={Boolean(confirmOpen && deletingContact)}
        onClose={setConfirmOpen}
        onConfirm={() => handleDeleteContact(deletingContact.id)}
      >
        {`${i18n.t("contacts.confirmationModal.deleteMessage")}`}
      </ConfirmationModal>
      <TicketRemindersModal
        modalOpen={contactActionModal.action === "reminders"}
        onClose={closeContactActionModal}
        ticket={contactActionModal.ticket}
      />
      <TicketAgendaModal
        modalOpen={contactActionModal.action === "agenda"}
        onClose={closeContactActionModal}
        ticket={contactActionModal.ticket}
      />
      <TicketOrdersModal
        modalOpen={contactActionModal.action === "orders"}
        onClose={closeContactActionModal}
        ticket={contactActionModal.ticket}
      />
      <TicketProcessesModal
        modalOpen={contactActionModal.action === "processes"}
        onClose={closeContactActionModal}
        ticket={contactActionModal.ticket}
      />
      <TicketStreamingModal
        modalOpen={contactActionModal.action === "streaming"}
        onClose={closeContactActionModal}
        ticket={contactActionModal.ticket}
      />
      <Dialog open={importModalOpen} onClose={closeImportModal} maxWidth="md" fullWidth>
        <DialogTitle>Importar contactos</DialogTitle>
        <DialogContent dividers>
          <div className={classes.importSteps}>
            <div>
              <input
                accept=".csv,.xml,.xlsx,.json,text/csv,text/xml,application/xml,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className={classes.fileInput}
                id="contacts-import-file"
                type="file"
                onChange={(event) => {
                  setImportFile(event.target.files?.[0] || null);
                  setImportPreview(null);
                }}
              />
              <label htmlFor="contacts-import-file">
                <Button variant="outlined" color="primary" component="span">
                  Seleccionar archivo
                </Button>
              </label>
              <Typography variant="body2" color="textSecondary">
                {importFile?.name || "CSV, XML, XLSX o JSON"}
              </Typography>
            </div>

            {importPreview && (
              <>
                <div className={classes.importSummary}>
                  <Typography variant="body2">
                    {importPreview.filename} - {importPreview.total} registros detectados
                  </Typography>
                </div>
                {importMappings.map(renderImportMappingRow)}
                <Button color="primary" onClick={addImportCustomMapping}>
                  Agregar campo personalizado
                </Button>
                <div className={classes.importSample}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {(importPreview.fields || []).slice(0, 6).map((field) => (
                          <TableCell key={field}>{field}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(importPreview.sampleRows || []).map((row, rowIndex) => (
                        <TableRow key={`sample-${rowIndex}`}>
                          {(importPreview.fields || []).slice(0, 6).map((field) => (
                            <TableCell key={field}>{row[field]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          {!importPreview ? (
            <Button
              onClick={handleImportPreview}
              color="primary"
              variant="contained"
              disabled={importLoading}
            >
              Revisar archivo
            </Button>
          ) : (
            <Button
              onClick={handleImportCommit}
              color="primary"
              variant="contained"
              disabled={importLoading}
            >
              Importar
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("contacts.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            {!hideResellerControls && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudDownloadIcon />}
                onClick={openImportModal}
              >
                Importar
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={handleOpenExportMenu}
            >
              Exportar
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleCloseExportMenu}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => handleExportContacts("csv")}>CSV</MenuItem>
              <MenuItem onClick={() => handleExportContacts("xml")}>XML</MenuItem>
              <MenuItem onClick={() => handleExportContacts("xlsx")}>XLSX</MenuItem>
              <MenuItem onClick={() => handleExportContacts("json")}>JSON</MenuItem>
            </Menu>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ViewColumnIcon />}
              onClick={(event) => setColumnsAnchorEl(event.currentTarget)}
            >
              Columnas
            </Button>
            {renderColumnsMenu()}
            {!hideResellerControls && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenContactModal}
              >
                Agregar
              </Button>
            )}
            <Tooltip title="Configurar Google Sheets">
              <IconButton
                color="primary"
                onClick={openSheetConfigModal}
                aria-label="Configurar Google Sheets"
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        {contacts.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allContactsSelected}
              indeterminate={selectedContactCount > 0 && !allContactsSelected}
              onChange={toggleAllContactsSelection}
              inputProps={{ "aria-label": "Seleccionar contactos" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedContactCount
                ? `${selectedContactCount} seleccionado${
                    selectedContactCount === 1 ? "" : "s"
                  }`
                : "Seleccionar contactos"}
            </Typography>
            {selectedContactCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={openBulkModal}
                >
                  Gestionar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedContactIds([])}
                >
                  Limpiar
                </Button>
                <Can
                  role={user.profile}
                  perform="contacts-page:deleteContact"
                  yes={() => (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => setBulkDeleteOpen(true)}
                    >
                      Eliminar
                    </Button>
                  )}
                />
              </>
            )}
          </div>
        )}
        <div className={classes.mobileCards}>
          {contacts.map(renderMobileContactCard)}
          {loading && <CircularProgress size={24} />}
        </div>

        <Table size="small" className={classes.tableWrapper}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {visibleColumns.map(renderColumnHeader)}
              <TableCell align="center">
                {i18n.t("contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={selectedContactIds.includes(String(contact.id))}
                      onChange={() => toggleContactSelection(contact.id)}
                      inputProps={{ "aria-label": `Seleccionar ${contact.name}` }}
                    />
                  </TableCell>
                  {visibleColumns.map(column => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={column.id === "avatar" ? { paddingRight: 0 } : undefined}
                    >
                      {renderContactColumnValue(contact, column)}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    {renderContactActions(contact)}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={visibleColumns.length + 2} />}
            </>
          </TableBody>
        </Table>
        <div className={classes.paginationBar}>
          <TablePagination
            component="div"
            count={contactCount}
            page={pageNumber - 1}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20, 30, 50]}
            labelRowsPerPage="Contactos por pagina"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
            }
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;

